import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import InputField from 'components/shared/input-field';
import PhoneNumberInput from 'components/shared/input-field/phoneInput';
import Button from 'components/shared/button';
import { Loader } from 'lucide-react';
import CountrySelect from 'components/shared/select-input/countryInput';
import SelectInput from 'components/shared/select-input';
import TermsModal from 'components/shared/modal/TermsModal';
import TermsContent from 'components/shared/TermsContent';
import useFetchStates from 'hooks/useFetchStates';
import PaySlipFileUpload from 'components/shared/input-field/PaySlipField';
import BankStatementUpload from 'components/shared/input-field/BankStatementFileUpload';
import Checkbox from 'components/shared/checkbox';
import CustomFileUpload from 'components/shared/input-field/FileUpload';

interface JobInfo {
  ParentRecords_Occupation: string;
  ParentRecords_CompanyName: string;
  ParentRecords_Location: string;
  Address2: string;
  City: string;
  PostalCode: string;
  Country: string;
  State: string;
  MonthsOfWorkinWithThem: string;
  ParentRecords_CompanyEmail: string;
  ParentRecords_CompanyPhoneNumber: string;
  ParentRecords_YearsOfWorkinWithThem: string;
  ParentRecords_PaySlip: File | null;
  ParentRecords_BankStatement: File | null;
  utilityBill: File | null;
  validId: File | null;
}

interface Step3Props {
  inputData: JobInfo;
  setInputData: (data: JobInfo) => void;
  errors: { [key: string]: string };
  clearErrors: (field: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  loading: boolean;
}

const validationSchema = yup.object({
  ParentRecords_Occupation: yup.string().required('Occupation is required'),
  ParentRecords_CompanyName: yup
    .string()
    .required("Company's name is required"),
  ParentRecords_CompanyPhoneNumber: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{13}$/, 'Phone number must be 13 digits including 234'),
  ParentRecords_Location: yup
    .string()
    .required("Company's address is required"),
  City: yup.string().required('City is required'),
  State: yup.string().required('State is required'),
  ParentRecords_YearsOfWorkinWithThem: yup
    .string()
    .required('Duration of work required'),
  ParentRecords_CompanyEmail: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  ParentRecords_PaySlip: yup
    .mixed()
    .nullable()
    .notRequired()
    .test(
      'fileSize',
      'File size is too large. Maximum size is 5MB.',
      (file: any) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      }
    ),

  ParentRecords_BankStatement: yup
    .mixed()

    .required('Bank statement is required')
    .test(
      'fileSize',
      'File size is too large. Maximum size is 5MB.',
      (file: any) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      }
    ),
  utilityBill: yup
    .mixed()

    .required('Utility bill is required')
    .test(
      'fileSize',
      'File size is too large. Maximum size is 5MB.',
      (value: any) => {
        return value && value.size <= 5 * 1024 * 1024;
      }
    ),
  validId: yup
    .mixed()

    .required('Valid ID is required')
    .test(
      'fileSize',
      'File size is too large. Maximum size is 5MB.',
      (value: any) => {
        return value && value.size <= 5 * 1024 * 1024;
      }
    ),
});

const MyChildLoanApplStep2: React.FC<Step3Props> = ({
  inputData,
  setInputData,
  errors,
  clearErrors,
  nextStep,
  prevStep,
  loading,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { data: stateOptions } = useFetchStates();

  const formik = useFormik({
    initialValues: inputData,
    validationSchema,
    onSubmit: (values) => {
      setInputData(values);
      clearErrors('');
    },
  });

  useEffect(() => {
    setInputData(formik.values);
  }, [formik.values]);

  const onDropUtilityBill = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.utilityBill || null;
      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'utilityBill',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('utilityBill', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError('utilityBill', 'You can only upload one payslip.');
        formik.setFieldTouched('utilityBill', true, false);
        return;
      } else {
        formik.setFieldValue('utilityBill', filteredFile);
        formik.setFieldError('utilityBill', '');
      }
    },
    [formik]
  );

  const onDropValidId = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.validId || null;
      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'validId',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('validId', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError('validId', 'You can only upload one payslip.');
        formik.setFieldTouched('validId', true, false);
        return;
      } else {
        formik.setFieldValue('validId', filteredFile);
        formik.setFieldError('validId', '');
      }
    },
    [formik]
  );
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === 'MonthsOfWorkinWithThem') {
      const numericValue = Math.max(1, Math.min(12, Number(value)));
      formik.setFieldValue(name, numericValue);
    } else if (name === 'State') {
      const selectedOption = stateOptions.find(
        (option) => option.value === value
      );
      if (selectedOption) {
        formik.setFieldValue(name, selectedOption.label); // Set the label (name) as the value
      }
    } else {
      formik.handleChange(e);
    }
    clearErrors(name);
  };

  const handleModalConfirm = (isChecked: boolean) => {
    setIsModalOpen(false);
    // Set the checkbox to true when modal is confirmed
    setIsChecked(isChecked);
  };

  const handleCheckboxChange = () => {
    if (isChecked) {
      setIsChecked(false);
    } else {
      setIsModalOpen(true);
    }
  };

  const removeDocument = (type: keyof JobInfo) => {
    formik?.setFieldValue(type, null);
    formik.setFieldTouched(type, false, false);
    formik.setFieldError(type, '');
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i),
    label: String(i),
  }));

  return (
    <div className="w-full scrollbar-always-visible">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl fomt-semibold">
          Parent Employment & Income Details
        </h1>
        <p className="text-lg text-gray-2">Complete the fields below</p>
      </div>
      <hr className="my-4" />
      <form onSubmit={formik.handleSubmit}>
        <div className="w-full  p-4 rounded grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className="col-span-1">
            <InputField
              type="text"
              label="Job/Occupation"
              name="ParentRecords_Occupation"
              placeholder="e.g Bank Manager"
              value={formik.values.ParentRecords_Occupation}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.ParentRecords_Occupation}
            />
          </div>

          <div className="col-span-1">
            <InputField
              type="text"
              label="Company's Name"
              name="ParentRecords_CompanyName"
              placeholder="Company's Name"
              value={formik.values.ParentRecords_CompanyName}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.ParentRecords_CompanyName}
            />
          </div>

          {/* Months and Years */}
          <div className="col-span-1">
            <label>
              Duration Of Working With the Company (Years & Month)
              <span className="text-red-600">*</span>
            </label>
            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <InputField
                  type="number"
                  label=""
                  name="ParentRecords_YearsOfWorkinWithThem"
                  placeholder="Years"
                  min={0}
                  value={formik.values.ParentRecords_YearsOfWorkinWithThem}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.ParentRecords_YearsOfWorkinWithThem}
                />
              </div>
              <div className="w-1/2">
                <SelectInput
                  label=""
                  name="MonthsOfWorkinWithThem"
                  options={monthOptions}
                  value={
                    monthOptions?.find(
                      (option) =>
                        option?.value === formik?.values?.MonthsOfWorkinWithThem
                    ) || null
                  }
                  onChange={(name, value) =>
                    formik?.setFieldValue(name, value?.value)
                  }
                  error={formik.errors.MonthsOfWorkinWithThem}
                />
              </div>
            </div>
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Parent Official Email Address"
              name="ParentRecords_CompanyEmail"
              placeholder="abc@example.com"
              value={formik.values.ParentRecords_CompanyEmail}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.ParentRecords_CompanyEmail}
            />
          </div>
          <div className="col-span-1">
            <PhoneNumberInput
              label="Contact Number For Company/HR"
              name="ParentRecords_CompanyPhoneNumber"
              inputValue={formik?.values?.ParentRecords_CompanyPhoneNumber}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              placeholder="eg. 0812345789"
              required
              error={formik.errors.ParentRecords_CompanyPhoneNumber}
            />
          </div>
          <CountrySelect
            value={formik.values.Country}
            setFieldValue={formik.setFieldValue}
            onChange={handleInputChange}
            placeholder="Choose Country"
            required={true}
          />
          <div className="col-span-1">
            <InputField
              type="text"
              label="Company's Address 1"
              name="ParentRecords_Location"
              placeholder="12 Ajo Lekki Phase 1"
              value={formik.values.ParentRecords_Location}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.ParentRecords_Location}
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Address 2(Optional)"
              name="Address2"
              placeholder="32 Ajo Lekki Phase 1"
              value={formik.values.Address2}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="City"
              name={`City`}
              placeholder="e.g Lagos"
              value={formik.values.City}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.City}
            />
          </div>
          <div className="col-span-1">
            <SelectInput
              label="State/Province/Region"
              options={stateOptions}
              required={true}
              name={`State`}
              value={
                stateOptions.find(
                  (option) => option?.label === formik.values.State
                ) || null
              }
              onChange={(name, value) => {
                formik.setFieldValue(name, value?.label);
              }}
              error={formik?.errors?.State}
            />
          </div>
          <PaySlipFileUpload
            label="3 Months Payslip"
            name="ParentRecords_PaySlip"
            formik={formik}
            removeDocument={removeDocument}
          />

          <BankStatementUpload
            label="6 Months Bank Statement"
            name="ParentRecords_BankStatement"
            formik={formik}
            removeDocument={removeDocument}
          />
          <CustomFileUpload
            label="Utility Bill"
            formik={formik}
            name="utilityBill"
            removeDocument={removeDocument}
            callbackfn={onDropUtilityBill}
          />
          <CustomFileUpload
            label="Valid ID"
            description="(International Passport, NIN Slip, Driver's Licence, or PVC)"
            formik={formik}
            name="validId"
            removeDocument={removeDocument}
            callbackfn={onDropValidId}
          />
        </div>
        <Checkbox
          id="terms-checkbox"
          label="I agree to the Terms and Conditions"
          checked={isChecked}
          onChange={handleCheckboxChange}
          className="mb-4"
        />
        <div className="flex justify-end gap-3 float-end">
          <Button
            type="button"
            variant="outlined"
            onClick={() => prevStep()}
            className="w-full"
          >
            Back
          </Button>
          <Button
            type="submit"
            // disabled={!formik.isValid}
            disabled={!isChecked || loading || !formik.isValid}
            onClick={nextStep}
            className="w-full"
          >
            {loading ? <Loader className="animate-spin" /> : 'Preview'}
          </Button>
        </div>
      </form>
      <TermsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Terms and Conditions"
        btnText="Agree"
        content={<TermsContent />}
        onConfirm={(isChecked) => handleModalConfirm(true)}
      />
    </div>
  );
};

export default MyChildLoanApplStep2;
